export type UUID = string;

export type ContentType = string;

export type Recnum = number;

export type Unit = 'pc' | 'm' | 'l' | 'kg' | 'm²';
export type OldUnit = 'S' | 'M' | 'L' | 'K' | 'O';

export function convertToOldUnit(unit: Unit | null): OldUnit | null {
  if (unit === null) {
    return null;
  }
  const rv: OldUnit | undefined = {
    pc: 'S',
    m: 'M',
    kg: 'K',
    'm²': 'O',
    l: 'L',
  }[unit] as any;
  if (!rv) {
    throw new Error(`Could not convert unit (${unit}) to old unit`);
  }
  return rv;
}

export function convertToNewUnit(unit: OldUnit | null): Unit | null {
  if (unit === null) {
    return null;
  }
  const rv: Unit | undefined = {
    S: 'pc',
    M: 'm',
    K: 'kg',
    O: 'm²',
    L: 'l',
  }[unit] as any;
  if (!rv) {
    throw new Error(`Could not convert unit (${unit}) to old unit`);
  }
  return rv;
}

// WorkorderProgress conversion functions
// Legacy codes: C=Concept, B=Digitale bon, G=Geen digitale bon, U=Uitgevoerd, V=Vervallen

export const WORKORDER_PROGRESS_OPTIONS = [
  { value: 'concept', label: 'Concept' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'scheduled_non_digital', label: 'Scheduled (Non-Digital)' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type WorkorderProgress =
  (typeof WORKORDER_PROGRESS_OPTIONS)[number]['value'];

export type OldWorkorderProgress = 'C' | 'B' | 'G' | 'U' | 'V';

export function convertToOldWorkorderProgress(
  progress: WorkorderProgress | null,
): OldWorkorderProgress | null {
  if (progress === null) {
    return null;
  }
  const rv: OldWorkorderProgress | undefined = {
    concept: 'C',
    scheduled: 'B',
    scheduled_non_digital: 'G',
    completed: 'U',
    cancelled: 'V',
  }[progress] as OldWorkorderProgress | undefined;
  if (!rv) {
    throw new Error(
      `Could not convert workorder progress (${progress}) to legacy code`,
    );
  }
  return rv;
}

export function convertToNewWorkorderProgress(
  progress: OldWorkorderProgress | null,
): WorkorderProgress | null {
  if (progress === null) {
    return null;
  }
  const rv: WorkorderProgress | undefined = {
    C: 'concept',
    B: 'scheduled',
    G: 'scheduled_non_digital',
    U: 'completed',
    V: 'cancelled',
  }[progress] as WorkorderProgress | undefined;
  if (!rv) {
    throw new Error(
      `Could not convert legacy code (${progress}) to workorder progress`,
    );
  }
  return rv;
}

// Settlement conversion functions
// Legacy codes: R=Regie, A=Aangenomen, O=Offerte, N=Nacalculatie, H=Archief, F=Gefactureerd, G=Garantie

export type Settlement =
  | 'cost_plus'
  | 'contracted'
  | 'quoted'
  | 'final_account'
  | 'archived'
  | 'invoiced'
  | 'warranty';

export type OldSettlement = 'R' | 'A' | 'O' | 'N' | 'H' | 'F' | 'G';

export function convertToOldSettlement(
  settlement: Settlement | null,
): OldSettlement | null {
  if (settlement === null) {
    return null;
  }
  const rv: OldSettlement | undefined = {
    cost_plus: 'R',
    contracted: 'A',
    quoted: 'O',
    final_account: 'N',
    archived: 'H',
    invoiced: 'F',
    warranty: 'G',
  }[settlement] as OldSettlement | undefined;
  if (!rv) {
    throw new Error(
      `Could not convert settlement (${settlement}) to legacy code`,
    );
  }
  return rv;
}

export function convertToNewSettlement(
  settlement: OldSettlement | null,
): Settlement | null {
  if (settlement === null) {
    return null;
  }
  const rv: Settlement | undefined = {
    R: 'cost_plus',
    A: 'contracted',
    O: 'quoted',
    N: 'final_account',
    H: 'archived',
    F: 'invoiced',
    G: 'warranty',
  }[settlement] as Settlement | undefined;
  if (!rv) {
    throw new Error(
      `Could not convert legacy code (${settlement}) to settlement`,
    );
  }
  return rv;
}
